<script setup>
import { nextTick, onMounted, onUnmounted, ref, useId, watch } from "vue";

defineOptions({ inheritAttrs: false });

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  panelId: { type: String, default: "" },
  triggerLabel: { type: String, required: true },
  panelLabel: { type: String, required: true },
  triggerClass: { type: [String, Array, Object], default: "" },
  panelClass: { type: [String, Array, Object], default: "" },
  panelTag: { type: String, default: "div" },
  disabled: { type: Boolean, default: false },
  persistent: { type: Boolean, default: false },
  floating: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue"]);

const generatedId = useId();
const resolvedPanelId = props.panelId || `ui-menu-${generatedId}`;
const rootRef = ref(null);
const triggerRef = ref(null);
const panelRef = ref(null);
const panelPosition = ref({ left: 0, top: 0 });
let pointerInside = false;
let floatingTriggerRect;

function positionFloatingMenu() {
  if (!props.floating || !props.modelValue) return;
  panelRef.value.showPopover();
  const trigger = triggerRef.value.getBoundingClientRect();
  floatingTriggerRect = trigger;
  const panel = panelRef.value.getBoundingClientRect();
  const below = trigger.bottom + 4;
  const top =
    below + panel.height <= window.innerHeight - 8
      ? below
      : trigger.top - panel.height - 4;
  panelPosition.value = {
    left: Math.max(
      8,
      Math.min(
        trigger.right - panel.width,
        window.innerWidth - panel.width - 8,
      ),
    ),
    top: Math.max(8, Math.min(top, window.innerHeight - panel.height - 8)),
  };
}

function onFloatingPointerDown(event) {
  if (props.floating) onDocumentMouseDown(event);
}

function onFloatingViewportChange(event) {
  if (!props.floating || !props.modelValue) return;
  if (event.target instanceof Node && panelRef.value.contains(event.target))
    return;
  const trigger = triggerRef.value.getBoundingClientRect();
  if (
    event.type === "resize" ||
    trigger.top !== floatingTriggerRect.top ||
    trigger.left !== floatingTriggerRect.left
  )
    closeMenu();
}

watch(() => props.modelValue, positionFloatingMenu, { flush: "post" });

function menuItems() {
  if (!panelRef.value) return [];
  return Array.from(
    panelRef.value.querySelectorAll(
      '[role="menuitem"]:not([aria-disabled="true"]), [role="menuitemradio"]:not([aria-disabled="true"]), [role="menuitemcheckbox"]:not([aria-disabled="true"])',
    ),
  ).filter((item) => !item.disabled);
}

async function openMenu(focusPosition) {
  if (props.disabled) return;
  emit("update:modelValue", true);
  if (!focusPosition) return;
  await nextTick();
  const items = menuItems();
  items[focusPosition === "last" ? items.length - 1 : 0]?.focus();
}

function closeMenu(restoreFocus = false) {
  emit("update:modelValue", false);
  if (restoreFocus) triggerRef.value?.focus();
}

function toggleMenu() {
  if (props.modelValue) closeMenu();
  else void openMenu();
}

function onDocumentMouseDown(event) {
  if (props.modelValue && !rootRef.value?.contains(event.target)) closeMenu();
}

function onTriggerKeydown(event) {
  if (event.key === "Escape" && props.modelValue) {
    event.preventDefault();
    event.stopPropagation();
    closeMenu(true);
    return;
  }
  if (!["ArrowDown", "ArrowUp"].includes(event.key)) return;
  event.preventDefault();
  void openMenu(event.key === "ArrowUp" ? "last" : "first");
}

function onPanelKeydown(event) {
  if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    closeMenu(true);
    return;
  }
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
  const items = menuItems();
  const currentIndex = items.indexOf(event.target);
  if (currentIndex === -1) return;
  event.preventDefault();
  let nextIndex;
  if (event.key === "Home") nextIndex = 0;
  else if (event.key === "End") nextIndex = items.length - 1;
  else if (event.key === "ArrowDown")
    nextIndex = (currentIndex + 1) % items.length;
  else nextIndex = (currentIndex - 1 + items.length) % items.length;
  items[nextIndex]?.focus();
}

function onFocusout(event) {
  if (
    !props.modelValue ||
    pointerInside ||
    !event.relatedTarget ||
    rootRef.value?.contains(event.relatedTarget)
  )
    return;
  closeMenu();
}

function clearPointerInside() {
  setTimeout(() => {
    pointerInside = false;
  }, 0);
}

onMounted(() => {
  positionFloatingMenu();
  document.addEventListener("pointerdown", onFloatingPointerDown, true);
  window.addEventListener("scroll", onFloatingViewportChange, true);
  window.addEventListener("resize", onFloatingViewportChange);
  document.addEventListener("mousedown", onDocumentMouseDown);
  window.addEventListener("pointerup", clearPointerInside);
  window.addEventListener("pointercancel", clearPointerInside);
});
onUnmounted(() => {
  document.removeEventListener("pointerdown", onFloatingPointerDown, true);
  window.removeEventListener("scroll", onFloatingViewportChange, true);
  window.removeEventListener("resize", onFloatingViewportChange);
  document.removeEventListener("mousedown", onDocumentMouseDown);
  window.removeEventListener("pointerup", clearPointerInside);
  window.removeEventListener("pointercancel", clearPointerInside);
});
</script>

<template>
  <div
    ref="rootRef"
    v-bind="$attrs"
    @focusout="onFocusout"
    @pointerdown.capture="pointerInside = true"
  >
    <button
      ref="triggerRef"
      type="button"
      class="min-h-11 min-w-11"
      :class="triggerClass"
      :aria-label="triggerLabel"
      aria-haspopup="menu"
      :aria-expanded="modelValue"
      :aria-controls="resolvedPanelId"
      :disabled="disabled"
      @click="toggleMenu"
      @keydown="onTriggerKeydown"
    >
      <slot name="trigger" :open="modelValue" />
    </button>
    <component
      :is="panelTag"
      v-if="modelValue || persistent"
      v-show="modelValue"
      :id="resolvedPanelId"
      ref="panelRef"
      role="menu"
      :aria-label="panelLabel"
      :class="panelClass"
      :popover="floating ? 'manual' : undefined"
      :style="
        floating
          ? {
              position: 'fixed',
              inset: 'auto',
              margin: 0,
              left: panelPosition.left + 'px',
              top: panelPosition.top + 'px',
              maxWidth: 'calc(100vw - 16px)',
              maxHeight: 'calc(100dvh - 16px)',
              overflowY: 'auto',
            }
          : undefined
      "
      @keydown="onPanelKeydown"
    >
      <slot name="panel" :close="closeMenu" />
    </component>
  </div>
</template>
